/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import moment from 'moment';
import numeral from 'numeral';
import _, { isEmpty, isNumber, isArray, pick, snakeCase, repeat } from 'lodash';

import {
  adjustPercent,
  difference,
  isPositiveValue,
  percentChange
} from './Calculation';
import { isPershingProduct } from './Products';
import { extractAndFormatOwners } from './Accounts';
import { ownershipMapping, tableTypes } from 'services/TableSupport';

export const NON_BREAKING_SPACE = '\u00a0';
export const notAvailable = 'n/a';
export const notAvailableFullText = 'Not available';
const DEFAULT_EXTENSION = '0000';
export const MONEY_MARKET_FORMAT = '$0,0.0000';
export const WHOLE_DOLLAR  = '$0,0';

export const titleCase = (str, separator = ' ', joiner = ' ') => {
  if (str === null) {
    return null;
  }
  return str
    .toLowerCase()
    .split(separator)
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(joiner);
};

export const plusMinusValue = (value, notAvailableValue = notAvailable) => {
  if (value === null) {
    return notAvailableValue;
  }
  return isPositiveValue(value) ? '+' + value : '' + value;
};

/**
 * @param {string} value
 * @param {string} notAvailableValue
 * @returns {string}
 */
export const plusMinusValueWithSpace = (
  value,
  notAvailableValue = notAvailable
) => {
  if (value === null) {
    return notAvailableValue;
  }
  return isPositiveValue(value) ? '+ ' + value : '' + value;
};

export const percentValue = (value) => {
  return numeral(adjustPercent(value)).format('0.00%');
};

export function accountName(account) {
  if (account == undefined) {
    return '';
  }

  // special case for active plus portfolio accounts
  if (account.productType === 110) {
    return account.accountName;
  }

  return (
    account.accountName +
    (isEmpty(account.ticker) ? '' : ` - ${account.ticker}`)
  );
}

/**
 * @param {Object} account
 * @returns {string|*}
 */
export function formatAccountNumber(account) {
  return isEmpty(account)
    ? ''
    : account.accountNumber +
        (isEmpty(account.accountChecksum) && !isNumber(account.accountChecksum)
          ? ''
          : `-${account.accountChecksum}`);
}

/**
 *
 * @param {string} initialTypeName
 * @param {string} formattedTypeName
 * @param {Object} account
 */
export const formatAccountType = (
  initialTypeName,
  formattedTypeName,
  account
) => {
  if (account && account.accountType) {
    account.accountType = account.accountType.replace(
      initialTypeName,
      formattedTypeName
    );
  }
};

// numeraljs has a bug that doesn't properly handle formatting small negative
// decimals i.e. -0.004
// https://stackoverflow.com/questions/48673348/numeral-js-negative-number-rounding-formatting-issue
function formatDollarAmountLocaleString(number, format) {
  //We do not want to round money market values
  if (format === MONEY_MARKET_FORMAT) {
    return numeral(number).format(format);
  }

  const floor = Math.round(number * 100) / 100;

  return numeral(
    floor.toLocaleString('en-us', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  ).format(format);
}

export function formatDollarAmount(number, format = '$0,0.00') {
  return formatDollarAmountLocaleString(number, format);
}

export function formatPercentAmount(number, withDecimal = true) {
  const format = withDecimal ? '0,0.00%' : '0,0%';
  return numeral(number).format(format);
}

/**
 *
 * @param {number} number - Number to be formatted
 * @param {number} precision - Expected precision for number
 * @param {boolean} withChar - Boolean flag that signifies if % character is appended to returned string
 * @returns {string}
 */
export function formatPercentPrecision(number, precision = 0, withChar = true) {
  const format = `0,0.${repeat('0', precision)}%`;
  const formatted = numeral(number).format(format);
  return withChar ? formatted : formatted.replace('%', '');
}

export const mapTransformedPositions = (positions, productType) => {
  return positions.map(mapAccountToDetail(productType));
};

const mapAccountToDetail =
  (productType) =>
  ({
    name: accountName,
    ticker,
    cusip,
    quantity,
    price,
    priceChange: change,
    currentBalance: marketValue,
    previousBalance
  }) => ({
    name: {
      accountName,
      ticker,
      cusip,
      productType
    },
    quantity,
    price,
    change,
    marketValue,
    balance: marketValue,
    dailyChange: createDailyChange(marketValue, previousBalance)
  });

export const capitalizedUnderscoreCase = (string) =>
  snakeCase(string).toUpperCase();

export const createCashRowData = ({ cash }) => {
  const currentBalance = cash && cash.currentBalance ? cash.currentBalance : 0;
  return {
    isCashRow: true,
    name: { accountName: 'CASH' },
    marketValue: currentBalance,
    balance: currentBalance,
    dailyChange: {
      difference: currentBalance - cash.previousBalance,
      percentChange:
        currentBalance - cash.previousBalance === 0 ||
        Math.abs(cash.previousBalance) === 0
          ? 0
          : (currentBalance - cash.previousBalance) /
            Math.abs(cash.previousBalance)
    }
  };
};

const collectDefaultDetails = (
  account,
  sweepAccountMap,
  accountPerformanceMap
) => {
  const defaultDetails = {
    isViewOnly: account.isViewOnly,
    hiddenAccount: account.hiddenAccount,
    name: {
      ...pick(
        account,
        'accountId',
        'accountName',
        'accountType',
        'cusipId',
        'owner',
        'sweepAccount'
      ),
      accountWChecksum: formatAccountNumber(account),
      accountChecksum: account.accountChecksum,
      accountNumber: account.accountNumber,
      accountTypeId: account.accountTypeId,
      moneyMarket: account.moneyMarket,
      availableBalance: account.availableBalance,
      ownerNames: extractAndFormatOwners(account),
      productType: account.productType,
      ticker: account.ticker,
      trpCode: account.trpCode,
      ownershipType: ownershipMapping(account),
      isClosed: account.closedAccount
    },
    productType: account.productType,
    performance: {
      percentReturn: null,
      inceptionDate: null
    }
  };

  const accountWithPerformance = accountPerformanceMap[account.accountId];
  if (accountWithPerformance) {
    if (accountWithPerformance.inceptionReturn !== null) {
      defaultDetails.performance.percentReturn = adjustPercent(
        accountWithPerformance.inceptionReturn
      );
    }
    defaultDetails.performance.inceptionDate =
      accountWithPerformance.inceptionDate;
  }
  const sweepAccountWChecksum = sweepAccountMap[account.accountId];
  if (sweepAccountWChecksum) {
    defaultDetails.name.sweepAccountWChecksum = sweepAccountWChecksum;
  }

  return defaultDetails;
};

const createDailyChange = (currentValue, previousValue) => ({
  difference: difference(currentValue, previousValue),
  percentChange: percentChange(currentValue, previousValue)
});

export const createAppBrokerageDetails = ({ account, defaultDetails }) => {
  const {
    positions,
    productType,
    balanceAsOfDate,
    balanceValue: marketValue,
    previousBalanceValue,
    isViewOnly,
  } = account;
  const asOfDate = balanceAsOfDate;
  defaultDetails.name.asOfDate = asOfDate;

  const details = {
    ...defaultDetails,
    dailyChange: createDailyChange(marketValue, previousBalanceValue),
    marketValue,
    balance: marketValue,
    actions: true,
    isViewOnly
  };
  const children = [
    ...mapTransformedPositions(positions, productType).map((positionDetails) => ({
      _type: tableTypes.HOLDING_ROW,
      metaData: {},
      details: {
        isViewOnly,
        ...positionDetails
      },
      children: []
    })),
    {
      _type: tableTypes.HOLDING_ROW,
      metaData: {},
      details: createCashRowData(account),
      children: []
    }
  ];

  return {
    _type: tableTypes.ACCOUNT_ROW,
    details,
    children
  };
};

const createMutualFundAnd529Details = ({ account, defaultDetails }) => {
  // check that there are positions to map to details
  const hasPositions =
    account && account.positions && account.positions.length > 0;
  const accountDetails = hasPositions
    ? mapAccountToDetail(account.productType)(account.positions[0])
    : {};

  return {
    _type: tableTypes.ACCOUNT_ROW,
    details: {
      ...accountDetails,
      ...defaultDetails,
      actions: true
    },
    children: []
  };
};

export const buildSweepMap = (accounts) => {
  const sweepMap = {};
  if (Array.isArray(accounts)) {
    accounts.forEach((account) => {
      const { sweepAccountId } = account;
      if (sweepAccountId) {
        const accountWChecksum = formatAccountNumber(account);
        sweepMap[sweepAccountId] = accountWChecksum;
      }
    });
  }
  return sweepMap;
};

export const buildPositionDetails = (
  accounts,
  accountDate,
  accountPerformanceMap = {}
) => {
  const sweepAccountMap = buildSweepMap(accounts);

  const positionDetails = accounts.reduce((acc, account) => {
    const defaultDetails = collectDefaultDetails(
      account,
      sweepAccountMap,
      accountPerformanceMap
    );

    const isBrokerageOrAPPOrRAS = isPershingProduct(account.productType);

    return acc.concat(
      isBrokerageOrAPPOrRAS
        ? createAppBrokerageDetails({
            account,
            defaultDetails,
            currentDate: accountDate
          })
        : createMutualFundAnd529Details({ account, defaultDetails })
    );
  }, []);

  return positionDetails;
};

export const arrayToListString = (arr) => arr.filter((v) => v).join(', ');

export const formatPhoneNumber = (phoneNumber) => {
  if (isEmpty(phoneNumber)) {
    return '';
  }
  const { areaCode, countryCode, exchange, extension, localNumber } =
    phoneNumber;
  if (!areaCode) {
    // foreign phone number
    return `
      ${countryCode}-${exchange}${localNumber}
    `;
  }
  return `
    (${areaCode}) ${exchange}-${localNumber}${
    extension ? ` Ext. ${extension}` : ''
  }
  `;
};

export const formatReadOnlyPhoneNumber = (phoneNumber) => {
  let cleaned = ('' + phoneNumber).replace(/\D/g, '');

  //Check if the input is of correct length
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }

  return null;
};

export const formatMailingAddressLocation = (mailing) => `
  ${mailing.city}, ${mailing.state} ${mailing.zipCode} - ${
  mailing.zipCodeExt ? mailing.zipCodeExt : DEFAULT_EXTENSION
}
`;

export const formatTaxNumber = (taxNumber) =>
  `••-•••${taxNumber ? taxNumber.substring(5) : '-****'}`;
export const formatSSN = (ssn) => `•••-••-${ssn ? ssn.substring(5) : '-****'}`;
export const maskPhoneNumber = (phone) =>
  `(***) ***-${phone ? phone.substring(6) : '****'}`;

export const buildMapFromArray = (array) =>
  array.reduce((accum, current) => {
    accum[current] = true;
    return accum;
  }, {});

export const csvFileNameFormat = (filename, date) => {
  if (date) {
    const formatYYYY_MM_DD = date ? date.substring(0, 10) : '';
    const dateArr = formatYYYY_MM_DD.split('-');
    const formatMM_DD_YYYY = dateArr[1] + '_' + dateArr[2] + '_' + dateArr[0];
    return `${filename}_${formatMM_DD_YYYY}.csv`;
  }
  return `${filename}_${moment().format('MM_DD_YYYY_hh_mm')}.csv`;
};

export const removeWhitespace = (value) => {
  if (value) {
    return value.replace(/\s/g, '');
  }
};

/* FUTURE - this can actually be used a lot of places if we standardize phone display - Trusted Contact, Contact Info etc */
export const extractPhoneNumber = (phoneObj) => {
  if (!phoneObj || isArray(phoneObj)) {
    return '';
  }
  const { areaCode, countryCode, exchange, localNumber } = phoneObj;
  return areaCode
    ? `${areaCode}-${exchange}-${localNumber}`
    : `${countryCode}-${areaCode}-${exchange}-${localNumber}`;
};

export const onlyNumbers = (rawInput) => {
  if (!rawInput) {
    return '';
  }
  return rawInput.trim().replace(/[^0-9]/g, '');
};

export const getPhoneNumberParts = (number) => {
  if (!number) {
    return {};
  }
  const cleanNumber = onlyNumbers(number);
  let countryCode = '1';
  let areaCode = '';
  let exchange = '';
  let localNumber = '';

  if (cleanNumber.length === 10) {
    areaCode = cleanNumber.substring(0, 3);
    exchange = cleanNumber.substring(3, 6);
    localNumber = cleanNumber.substring(6, cleanNumber.length);
  }
  if (cleanNumber.length === 11) {
    countryCode = cleanNumber.substring(0, 1);
    areaCode = cleanNumber.substring(1, 4);
    exchange = cleanNumber.substring(4, 7);
    localNumber = cleanNumber.substring(7, cleanNumber.length);
  }

  return {
    countryCode,
    areaCode,
    exchange,
    localNumber
  };
};

export const filterObjectFromWhiteList = (obj, whitelistedKeys) => {
  return Object.keys(obj)
    .filter((key) => whitelistedKeys.includes(key))
    .reduce((cur, key) => { return Object.assign(cur, { [key]: obj[key] })}, {});
};

//if any values of an object are null or undefined
//return true otherwise return false
export const doesObjectPropertyNotHaveValue = (obj) => {
  const objValues = Object.values(obj);

  if (_.includes(objValues, null) || _.includes(objValues, undefined)) {
    return true;
  } else {
    return false;
  }
}